// /src/ui/components/PartInspector.tsx

import React, { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { useModelStore, selectSelectedPart, selectSelectedInstance, MAIN_SUBMODEL_ID } from '@/store/modelStore';
import { COLORS, colorToHex } from '@/engine/ldrawColors';

function decompose(matrixArr: number[]) {
  const m = new THREE.Matrix4().fromArray(matrixArr);
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  m.decompose(pos, quat, scl);
  const euler = new THREE.Euler().setFromQuaternion(quat, 'XYZ');
  return { pos, euler };
}

function buildMatrix(pos: THREE.Vector3, euler: THREE.Euler) {
  const q = new THREE.Quaternion().setFromEuler(euler);
  const m = new THREE.Matrix4().compose(pos, q, new THREE.Vector3(1, 1, 1));
  return m.elements.slice();
}

export default function PartInspector() {
  const selectedPart = useModelStore(selectSelectedPart);
  const selectedInstance = useModelStore(selectSelectedInstance);
  const mode = useModelStore((s) => s.mode);
  const uiMode = useModelStore((s) => s.uiMode);
  const submodels = useModelStore((s) => s.submodels);
  const updatePartProps = useModelStore((s) => s.updatePartProps);
  const updateInstanceProps = useModelStore((s) => s.updateInstanceProps);
  const pushToast = useModelStore((s) => s.pushToast);

  const [px, setPx] = useState('0');
  const [py, setPy] = useState('0');
  const [pz, setPz] = useState('0');
  const [rx, setRx] = useState('0');
  const [ry, setRy] = useState('0');
  const [rz, setRz] = useState('0');

  const selectedKind = selectedPart ? 'part' : selectedInstance ? 'instance' : null;
  const selectedMatrix = selectedPart?.matrix ?? selectedInstance?.matrix ?? null;

   useEffect(() => {
    if (!selectedMatrix) return;
    const { pos, euler } = decompose(selectedMatrix);
    const d = (r: number) => (THREE.MathUtils.radToDeg(r)).toFixed(2);
    setPx(pos.x.toFixed(2));
    setPy(pos.y.toFixed(2));
    setPz(pos.z.toFixed(2));
    setRx(d(euler.x));
    setRy(d(euler.y));
    setRz(d(euler.z));
  }, [selectedPart?.id, selectedInstance?.id]);

  const colorHex = useMemo(
    () => (selectedPart ? (selectedPart.colorHex ?? colorToHex(selectedPart.ldrawColor)) : '#c0c0c0'),
    [selectedPart]
  );

  const instanceSubmodelName = useMemo(() => {
    if (!selectedInstance) return '';
    return submodels.find((sm) => sm.id === selectedInstance.submodelId)?.name ?? selectedInstance.submodelId;
  }, [selectedInstance?.submodelId, submodels]);

  const instanceParentName = useMemo(() => {
    if (!selectedInstance) return '';
    const pid = selectedInstance.parentSubmodelId ?? MAIN_SUBMODEL_ID;
    if (pid === MAIN_SUBMODEL_ID) return 'Main model';
    return submodels.find((sm) => sm.id === pid)?.name ?? pid;
  }, [selectedInstance?.parentSubmodelId, submodels]);

  const instanceSubmodelOptions = useMemo(() => {
    if (!selectedInstance) return [];
    const parentId = selectedInstance.parentSubmodelId ?? MAIN_SUBMODEL_ID;
    // Match the insert UI constraints:
    // - don't allow MAIN as a child ref
    // - don't allow referencing the current parent submodel (direct self recursion)
    // (but always keep the current value present if the model already contains it)
    return submodels
      .filter((sm) => sm.id !== MAIN_SUBMODEL_ID)
      .filter((sm) => sm.id !== parentId || sm.id === selectedInstance.submodelId);
  }, [selectedInstance?.submodelId, selectedInstance?.parentSubmodelId, submodels]);

  const instanceParentOptions = useMemo(() => {
    if (!selectedInstance) return [];
    const childId = selectedInstance.submodelId;
    // Don't allow placing an instance inside the same submodel it references (direct recursion),
    // but keep current value present if the model already contains it.
    return submodels.filter((sm) => sm.id !== childId || sm.id === selectedInstance.parentSubmodelId);
  }, [selectedInstance?.submodelId, selectedInstance?.parentSubmodelId, submodels]);
 
   const commit = () => {
    if (!selectedKind) return;
    if (uiMode !== 'edit') return;
    if (!selectedMatrix) return;
    const { pos, euler } = decompose(selectedMatrix);
    const num = (s: string) => {
      const v = Number(s);
      return Number.isFinite(v) ? v : 0;
    };
    pos.set(num(px), num(py), num(pz));
    euler.set(
      THREE.MathUtils.degToRad(num(rx)),
      THREE.MathUtils.degToRad(num(ry)),
      THREE.MathUtils.degToRad(num(rz))
    );
    const nextMatrix = buildMatrix(pos, euler);
    if (selectedKind === 'part' && selectedPart) {
      updatePartProps(selectedPart.id, { matrix: nextMatrix } as any);
    } else if (selectedKind === 'instance' && selectedInstance) {
      updateInstanceProps(selectedInstance.id, { matrix: nextMatrix } as any);
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="panelHeader">
        <div>
          <div className="title">Inspector</div>
          <div className="hint">Selection, transform, color, steps.</div>
        </div>
      </div>
      <div className="panelBody">
        {!selectedKind ? (
          <div className="card">
            <div style={{ fontSize: 13, marginBottom: 6 }}>Nothing selected</div>
            <div className="muted">Click a part or instance in the 3D view to select it. Use W/E to switch gizmos.</div>
            <div className="hr" />
            <div className="help">
              <b>Tip:</b> In {mode} mode, snapping is controlled from the top bar.
              {uiMode === 'instructions' && <> · You are in <b>Instruction mode</b> (editing disabled).</>}
            </div>
          </div>
        ) : selectedKind === 'part' && selectedPart ? (
          <>
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="swatch" style={{ background: colorHex, width: 44, height: 36 }} />
                <div>
                  <div style={{ fontSize: 13 }}>{selectedPart.name}</div>
              <div className="muted mono">{selectedPart.partFile}</div>
                 </div>
              </div>

              <div className="hr" />

              <div className="field">
                <label>Color</label>
                <select
                  value={selectedPart.ldrawColor}
                  disabled={uiMode !== 'edit'}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    updatePartProps(selectedPart.id, { ldrawColor: id, colorHex: colorToHex(id) });
                  }}
                >
                  {COLORS.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.id})</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Step</label>
                <input
                  type="number"
                  min={1}
                  value={selectedPart.step}
                  disabled={uiMode !== 'edit'}
                  onChange={(e) => updatePartProps(selectedPart.id, { step: Math.max(1, Number(e.target.value)) })}
                />
              </div>

              <div className="field">
                <label>Submodel</label>
                <select
                  value={(selectedPart as any).submodelId ?? MAIN_SUBMODEL_ID}
                  disabled={uiMode !== 'edit'}
                  onChange={(e) => {
                    const id = String(e.target.value);
                    updatePartProps(selectedPart.id, { submodelId: id } as any);
                  }}
                >
                  {submodels.map((sm) => (
                    <option key={sm.id} value={sm.id}>
                      {sm.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ height: 12 }} />

            <div className="card">
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Transform (LDU)</div>
              <div className="field"><label>X</label><input value={px} disabled={uiMode !== 'edit'} onChange={(e) => setPx(e.target.value)} onBlur={commit} /></div>
              <div className="field"><label>Y</label><input value={py} disabled={uiMode !== 'edit'} onChange={(e) => setPy(e.target.value)} onBlur={commit} /></div>
              <div className="field"><label>Z</label><input value={pz} disabled={uiMode !== 'edit'} onChange={(e) => setPz(e.target.value)} onBlur={commit} /></div>
              <div className="hr" />
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Rotation (deg)</div>
              <div className="field"><label>Rx</label><input value={rx} disabled={uiMode !== 'edit'} onChange={(e) => setRx(e.target.value)} onBlur={commit} /></div>
              <div className="field"><label>Ry</label><input value={ry} disabled={uiMode !== 'edit'} onChange={(e) => setRy(e.target.value)} onBlur={commit} /></div>
              <div className="field"><label>Rz</label><input value={rz} disabled={uiMode !== 'edit'} onChange={(e) => setRz(e.target.value)} onBlur={commit} /></div>

              {mode === 'advanced' && (
                <>
                  <div className="hr" />
                  <div className="muted">
                    Advanced mode will eventually expose connector snapping, hinge solvers, and submodel trees.
                    This prototype focuses on the LDraw-first editing backbone.
                  </div>
                </>
              )}

              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button
                  className="btn"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedPart.partFile);
                    pushToast({ title: 'Copied', detail: selectedPart.partFile, kind: 'ok' });
                  }}
                >
                  Copy part id
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(selectedPart.matrix));
                    pushToast({ title: 'Copied', detail: 'Matrix copied to clipboard.', kind: 'ok' });
                  }}
                >
                  Copy matrix
                </button>
              </div>
            </div>
          </>
        ) : selectedKind === 'instance' && selectedInstance ? (
          <>
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="swatch" style={{ width: 44, height: 36 }} />
                <div>
                  <div style={{ fontSize: 13 }}>Instance</div>
                  <div className="muted mono">{selectedInstance.id}</div>
                </div>
              </div>

              <div className="hr" />

              <div className="muted">
                <span className="muted">submodel:</span> <span className="mono">{instanceSubmodelName}</span> ·{' '}
                <span className="muted">parent:</span> <span className="mono">{instanceParentName}</span>
              </div>

              <div className="hr" />

              <div className="field">
                <label>Submodel</label>
                <select
                  value={selectedInstance.submodelId}
                  disabled={uiMode !== 'edit'}
                  onChange={(e) => {
                    const id = String(e.target.value);
                    updateInstanceProps(selectedInstance.id, { submodelId: id } as any);
                  }}
                  title="Which submodel definition this instance references"
                >
                  {instanceSubmodelOptions.map((sm) => (
                    <option key={sm.id} value={sm.id}>
                      {sm.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Parent</label>
                <select
                  value={selectedInstance.parentSubmodelId ?? MAIN_SUBMODEL_ID}
                  disabled={uiMode !== 'edit'}
                  onChange={(e) => {
                    const id = String(e.target.value);
                    updateInstanceProps(selectedInstance.id, { parentSubmodelId: id } as any);
                  }}
                  title="Which submodel this instance lives inside"
                >
                  {instanceParentOptions.map((sm) => (
                    <option key={sm.id} value={sm.id}>
                      {sm.id === MAIN_SUBMODEL_ID ? 'Main model' : sm.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Step</label>
                <input
                  type="number"
                  min={1}
                  value={selectedInstance.step}
                  disabled={uiMode !== 'edit'}
                  onChange={(e) =>
                    updateInstanceProps(selectedInstance.id, { step: Math.max(1, Number(e.target.value)) } as any)
                  }
               />
              </div>

              <div className="help" style={{ marginTop: 8 }}>
                Instances place an entire submodel into a parent context. Transform edits move the whole submodel as a unit.
                {uiMode === 'instructions' && <> · You are in <b>Instruction mode</b> (editing disabled).</>}
              </div>
            </div>

            <div style={{ height: 12 }} />

            <div className="card">
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Transform (LDU)</div>
              <div className="field"><label>X</label><input value={px} disabled={uiMode !== 'edit'} onChange={(e) => setPx(e.target.value)} onBlur={commit} /></div>
              <div className="field"><label>Y</label><input value={py} disabled={uiMode !== 'edit'} onChange={(e) => setPy(e.target.value)} onBlur={commit} /></div>
              <div className="field"><label>Z</label><input value={pz} disabled={uiMode !== 'edit'} onChange={(e) => setPz(e.target.value)} onBlur={commit} /></div>
              <div className="hr" />
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Rotation (deg)</div>
              <div className="field"><label>Rx</label><input value={rx} disabled={uiMode !== 'edit'} onChange={(e) => setRx(e.target.value)} onBlur={commit} /></div>
              <div className="field"><label>Ry</label><input value={ry} disabled={uiMode !== 'edit'} onChange={(e) => setRy(e.target.value)} onBlur={commit} /></div>
              <div className="field"><label>Rz</label><input value={rz} disabled={uiMode !== 'edit'} onChange={(e) => setRz(e.target.value)} onBlur={commit} /></div>

              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button
                  className="btn"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedInstance.id);
                    pushToast({ title: 'Copied', detail: selectedInstance.id, kind: 'ok' });
                  }}
                >
                  Copy instance id
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(selectedInstance.matrix));
                    pushToast({ title: 'Copied', detail: 'Matrix copied to clipboard.', kind: 'ok' });
                  }}
                >
                  Copy matrix
                </button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
